import { useModal } from "@flaminem/ui-components";
import { FC, useRef } from "react";

import { useProgressStepModal, ProgressStepModal, ModalTransition } from "src/components/Modal";
import { useT } from "src/internationalization";
import { isDefinedScenarioRule, ShortScenario } from "src/types/scenario.ts";
import { cn } from "src/utils/string.utils.ts";

import { RulesForm, RulesFormRef } from "../../../components/scenario/RulesForm.tsx";

import { ConfirmScenarioVersionCreationModal } from "./ConfirmScenarioVersionCreationModal.tsx";
import {
  CreateScenarioVersionForm,
  CreateScenarioVersionFormRef,
} from "./CreateScenarioVersionForm.tsx";
import { CreateVersionData } from "./CreateVersion.types.ts";

export type CreateScenarioVersionModalProps = {
  onClose: () => void;
  onConfirm: () => void;
  scenario: ShortScenario;
};

export const CreateScenarioVersionModal: FC<CreateScenarioVersionModalProps> = ({
  onClose,
  onConfirm,
  scenario,
}) => {
  const t = useT();

  const submitVersionForm = () => {
    createScenarioVersionFormRef.current?.submit(
      (data) => {
        dataRef.current = { ...dataRef.current, ...data };
        stepsControl.setStepError("version", false);
        stepsControl.goNextStep();
      },
      () => {
        stepsControl.setStepError("version", true);
      }
    )();
  };

  const submitRulesForm = () => {
    scenarioVersionRulesFormRef.current?.submit(
      async (formData) => {
        dataRef.current.rules = formData;
        stepsControl.setStepError("rules", false);
        openModal();
      },
      () => {
        stepsControl.setStepError("rules", true);
      }
    )();
  };

  const { modalProps, control: stepsControl } = useProgressStepModal([
    {
      type: "version",
      title: t("create.scenario.version.modal.version.tab.name"),
      subtitle: t("create.scenario.version.modal.version.tab.subtitle"),
      submit: submitVersionForm,
      focus: "#versionName",
    },
    {
      type: "rules",
      title: t("rules.title"),
      subtitle: t("create.scenario.modal.rules.tab.subtitle"),
      submit: submitRulesForm,
      focus: "#searchBlacklistable",
    },
  ]);

  const createScenarioVersionFormRef = useRef<CreateScenarioVersionFormRef | null>(null);
  const scenarioVersionRulesFormRef = useRef<RulesFormRef | null>(null);

  const dataRef = useRef<CreateVersionData>({
    rules: [],
    name: "",
    threshold: 0,
    description: "",
  });

  const { isModalOpen, openModal, closeModal } = useModal();

  return (
    <>
      <ProgressStepModal
        kind="horizontal"
        onClose={onClose}
        title={t("create.scenario.version.modal.title")}
        subtitle={scenario.name}
        noPaddingX
        confirmButtonText={t("general.actions.create")}
        {...modalProps}
      >
        <div
          className={cn("flex flex-col px-5 h-full", {
            hidden: !stepsControl.isStep("version"),
          })}
        >
          <CreateScenarioVersionForm
            scenario={scenario}
            ref={createScenarioVersionFormRef}
            onValidChange={(isValid) => stepsControl.setStepError("version", !isValid)}
          />
        </div>
        <div
          className={cn("flex flex-col h-full overflow-hidden", {
            hidden: !stepsControl.isStep("rules"),
          })}
        >
          <RulesForm
            caseTypeCode={scenario.caseType.code}
            defaultScenarioRules={scenario.rules.filter(isDefinedScenarioRule)}
            title={t("create.scenario.version.modal.rules.tab.title")}
            subtitle={t("create.scenario.modal.rules.tab.description")}
            ref={scenarioVersionRulesFormRef}
            onValidChange={(isValid) => stepsControl.setStepError("rules", !isValid)}
          />
        </div>
      </ProgressStepModal>
      <ModalTransition>
        {isModalOpen && (
          <ConfirmScenarioVersionCreationModal
            versionData={dataRef.current}
            scenario={scenario}
            onClose={closeModal}
            onSubmit={() => {
              closeModal();
              onConfirm();
            }}
          />
        )}
      </ModalTransition>
      ,=
    </>
  );
};
